/**
 * Componente Alert
 *
 * Exibe mensagens de alerta (sucesso, erro, aviso, info).
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { clsx } from 'clsx';
import {
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  X,
} from 'lucide-react';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  /** Variante do alerta */
  variant: AlertVariant;
  /** Título do alerta (opcional) */
  title?: string;
  /** Mensagem do alerta */
  children: React.ReactNode;
  /** Callback ao fechar o alerta (opcional) */
  onClose?: () => void;
  /** Classes CSS adicionais */
  className?: string;
}

const variantStyles: Record<AlertVariant, string> = {
  success: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300',
  error: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
  info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300',
};

const variantIcons: Record<AlertVariant, React.ElementType> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconStyles: Record<AlertVariant, string> = {
  success: 'text-green-500 dark:text-green-400',
  error: 'text-red-500 dark:text-red-400',
  warning: 'text-yellow-500 dark:text-yellow-400',
  info: 'text-blue-500 dark:text-blue-400',
};

/**
 * Componente Alert
 */
export function Alert({
  variant,
  title,
  children,
  onClose,
  className,
}: AlertProps) {
  const Icon = variantIcons[variant];

  return (
    <div
      role="alert"
      className={clsx(
        'flex items-start gap-3 rounded-lg border p-4',
        variantStyles[variant],
        className,
      )}
    >
      <Icon className={clsx('h-5 w-5 flex-shrink-0 mt-0.5', iconStyles[variant])} />

      <div className="flex-1 min-w-0">
        {title && (
          <h4 className="font-semibold mb-1">{title}</h4>
        )}
        <div className="text-sm">{children}</div>
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className={clsx(
            'flex-shrink-0 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            variant === 'success' && 'focus:ring-green-500',
            variant === 'error' && 'focus:ring-red-500',
            variant === 'warning' && 'focus:ring-yellow-500',
            variant === 'info' && 'focus:ring-blue-500',
          )}
          aria-label="Fechar alerta"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
